import { Button, Card, CardContent } from '@design-system';
import { consumerQuoteCarrier, consumerQuoteKey } from '@shared/consumerPlan';
import { PolicyTermsList } from '../components/policy-terms-list';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useCoverageSelection } from '../hooks/use-coverage-selection';
import { OPTION_LABELS } from '../lib/copy';
import { premiumText } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

export function CompareStep({ clientId }: ConsumerStepProps) {
  const { finish } = useConsumerFlow(clientId);
  const { options, projections, plan, updatePlan, scenario } = useCoverageSelection(clientId);
  if (!scenario) return null;

  const choose = (selectedQuoteKey: string) => {
    updatePlan({ selectedQuoteKey });
    finish('options');
  };

  return (
    <StepFrame
      step="compare"
      title="Which trade-off matters most to you?"
      subtitle="The same payment schedule, with the terms side by side."
      onBack={() => finish('options')}
      help={<p>Compare the waiting period, benefit growth, and payment type before deciding. These options don’t have equivalent benefits.</p>}
      primary={{ label: 'Return to options', onClick: () => finish('options') }}
    >
      <div className="grid gap-4 md:grid-cols-3">
        {options.map(option => {
          const key = consumerQuoteKey(option.quote);
          return (
            <Card key={key} className="gap-3 py-4">
              <CardContent className="flex flex-col gap-3 px-4">
                <span className="text-primary text-xs font-semibold uppercase tracking-wide">
                  {OPTION_LABELS[option.label]}
                </span>
                <strong className="text-gray-950">
                  {consumerQuoteCarrier(option.quote)} · {option.quote.productName}
                </strong>
                <span className="text-muted-foreground text-sm">{premiumText(option.quote)}</span>
                <PolicyTermsList
                  quote={option.quote}
                  projection={projections.get(key)}
                  careStartAge={scenario.careStartAge}
                  yearsUntilCare={scenario.yearsUntilCare}
                />
                <Button variant="outline" onClick={() => choose(key)}>
                  {plan?.selectedQuoteKey === key ? 'Keep this option' : 'Choose this option'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </StepFrame>
  );
}
